import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import apiClient from '../../api/client';
import DoseResponseChart from '../../components/chemistry/compound-tabs/DoseResponseChart';
import RegistrationSection from '../../components/chemistry/compound-tabs/RegistrationSection';
import ELNSection from '../../components/chemistry/compound-tabs/ELNSection';
import StructureImage from '../../components/chemistry/StructureImage';
import { useUIStore } from '../../stores/uiStore';

// =====================================================================
// Types
// =====================================================================

interface BatchRow {
  batch_id: number;
  batch_code: string;
  chemist_initials: string;
  smiles: string;
  inchi_key: string | null;
  status: string;
  purity_percent: number | null;
  amount_mg: number | null;
  synthesis_date: string | null;
  created_at: string | null;
  notes: string | null;
}

interface BatchDetail {
  batch_id: number;
  batch_code: string;
  chemist_initials: string;
  notebook_number: number;
  salt_suffix: string | null;
  smiles: string;
  inchi_key: string | null;
  project_id: number;
  status: string;
  purity_percent: number | null;
  amount_mg: number | null;
  synthesis_date: string | null;
  created_at: string | null;
  registered_lot_id: number | null;
  notes: string | null;
  submissions: Submission[];
  statistics: Stat[];
  registration: { parent_code: string; version_code: string; lot_code: string } | null;
}

interface Submission {
  submission_id: number;
  assay_name: string;
  submitted_at: string | null;
  status: string;
  completed_at: string | null;
  notes: string | null;
}

interface Stat {
  stat_id: number;
  assay_name: string;
  measurement_type: string;
  n_replicates: number | null;
  mean_value: number | null;
  std_deviation: number | null;
  ci_low: number | null;
  ci_high: number | null;
  curve_fit_r2: number | null;
  hill_slope: number | null;
}

interface Project {
  id: number;
  name: string;
}

// =====================================================================
// Helpers
// =====================================================================

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active:     { bg: '#374151', text: '#ffffff' },
  submitted:  { bg: '#92400e', text: '#ffffff' },
  registered: { bg: '#065f46', text: '#ffffff' },
  retired:    { bg: '#6b7280', text: '#ffffff' },
};

const SUB_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  pending:     { bg: '#374151', text: '#d1d5db' },
  in_progress: { bg: '#92400e', text: '#fbbf24' },
  completed:   { bg: '#065f46', text: '#6ee7b7' },
  failed:      { bg: '#7f1d1d', text: '#fca5a5' },
};

function r2Color(r2: number | null): string {
  if (r2 === null) return '#6b7280';
  if (r2 > 0.99) return '#10b981';
  if (r2 > 0.95) return '#f59e0b';
  return '#ef4444';
}

function formatDate(iso: string | null): string {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

// =====================================================================
// Component
// =====================================================================

type TabId = 'all' | 'active' | 'submitted' | 'registered' | 'retired';

const TABS: { id: TabId; label: string }[] = [
  { id: 'all', label: 'All Batches' },
  { id: 'active', label: 'Active' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'registered', label: 'Registered' },
  { id: 'retired', label: 'Retired' },
];

const BatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const showNotification = useUIStore((s) => s.showNotification);

  // Project selection
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);

  // Batch list
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Expanded batch detail
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [detail, setDetail] = useState<BatchDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Chart data
  const [chartData, setChartData] = useState<any>(null);
  const [chartLoading, setChartLoading] = useState(false);

  // Detail refresh (for registration)
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  // Create batch modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createInitials, setCreateInitials] = useState('');
  const [createSmiles, setCreateSmiles] = useState('');
  const [createProjectId, setCreateProjectId] = useState<number | null>(null);
  const [createPurity, setCreatePurity] = useState('');
  const [createAmount, setCreateAmount] = useState('');
  const [createDate, setCreateDate] = useState(new Date().toISOString().slice(0, 10));
  const [createNotes, setCreateNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Stats
  const totalCount = batches.length;
  const statusCounts = batches.reduce<Record<string, number>>((acc, b) => {
    acc[b.status] = (acc[b.status] || 0) + 1;
    return acc;
  }, {});

  // ─── Load projects ───
  useEffect(() => {
    apiClient.get('/projects/')
      .then(resp => {
        const pList = (resp.data || []).map((p: any) => ({ id: p.id, name: p.name || p.project_name || `Project ${p.id}` }));
        setProjects(pList);
        if (pList.length > 0 && !selectedProject) {
          setSelectedProject(pList[0].id);
        }
      })
      .catch(() => {});
  }, []);

  // ─── Load batches ───
  const fetchBatches = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true); setError(null);
    try {
      const resp = await apiClient.get('/batches/list', { params: { project_id: selectedProject } });
      setBatches(resp.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load batches');
    } finally { setLoading(false); }
  }, [selectedProject]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  // ─── Create batch handler ───
  const handleCreateBatch = async () => {
    if (!createInitials.trim() || !createSmiles.trim() || !createProjectId) return;
    setCreating(true);
    setCreateError(null);
    try {
      const body: Record<string, any> = {
        chemist_initials: createInitials.trim().toUpperCase(),
        smiles: createSmiles.trim(),
        project_id: createProjectId,
      };
      if (createPurity) body.purity_percent = parseFloat(createPurity);
      if (createAmount) body.amount_mg = parseFloat(createAmount);
      if (createDate) body.synthesis_date = createDate;
      if (createNotes.trim()) body.notes = createNotes.trim();
      const resp = await apiClient.post('/batches/create', body);
      showNotification('success', `Batch ${resp.data.batch_code} created`);
      setShowCreateModal(false);
      setCreateInitials(''); setCreateSmiles(''); setCreatePurity('');
      setCreateAmount(''); setCreateNotes(''); setCreateError(null);
      fetchBatches();
    } catch (err: any) {
      setCreateError(err?.response?.data?.detail || 'Failed to create batch');
    } finally {
      setCreating(false);
    }
  };

  // ─── Load detail ───
  useEffect(() => {
    if (!expandedCode) { setDetail(null); return; }
    const fetchDetail = async () => {
      setDetailLoading(true);
      try {
        const resp = await apiClient.get(`/batches/${expandedCode}`);
        setDetail(resp.data);
      } catch { setDetail(null); }
      finally { setDetailLoading(false); }
    };
    fetchDetail();
  }, [expandedCode, detailRefreshKey]);

  // ─── Load chart data when detail has stats ───
  useEffect(() => {
    if (!expandedCode || !detail || detail.statistics.length === 0) {
      setChartData(null);
      return;
    }
    const fetchChartData = async () => {
      setChartLoading(true);
      try {
        const resp = await apiClient.get(`/batches/assays/${expandedCode}`);
        setChartData(resp.data);
      } catch { setChartData(null); }
      finally { setChartLoading(false); }
    };
    fetchChartData();
  }, [expandedCode, detail]);

  // ─── Filter batches ───
  const filteredBatches = batches.filter(b => {
    if (activeTab !== 'all' && b.status !== activeTab) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return b.batch_code.toLowerCase().includes(q)
        || b.chemist_initials.toLowerCase().includes(q)
        || (b.smiles && b.smiles.toLowerCase().includes(q))
        || (b.notes && b.notes.toLowerCase().includes(q));
    }
    return true;
  });

  const handleRowClick = (batchCode: string) => {
    setExpandedCode(prev => prev === batchCode ? null : batchCode);
    setChartData(null);
  };

  // ─── Render expanded detail panel ───
  const renderDetail = () => {
    if (detailLoading) return <div style={{ padding: 16, color: '#9ca3af', fontSize: 13 }}>Loading details...</div>;
    if (!detail) return <div style={{ padding: 16, color: '#ef4444', fontSize: 13 }}>Failed to load</div>;

    return (
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Registration */}
        <RegistrationSection
          batchCode={detail.batch_code}
          status={detail.status}
          inchiKey={detail.inchi_key}
          registration={detail.registration}
          onRegistered={() => { setDetailRefreshKey(k => k + 1); }}
        />

        {/* Metadata grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
          <MetaCell label="Chemist" value={detail.chemist_initials} />
          <MetaCell label="Notebook #" value={String(detail.notebook_number)} />
          <MetaCell label="Status" value={detail.status} />
          <MetaCell label="Purity" value={detail.purity_percent != null ? `${detail.purity_percent}%` : '\u2014'} />
          <MetaCell label="Amount" value={detail.amount_mg != null ? `${detail.amount_mg} mg` : '\u2014'} />
          <MetaCell label="Created" value={formatDate(detail.created_at)} />
          {detail.synthesis_date && <MetaCell label="Synthesized" value={formatDate(detail.synthesis_date)} />}
        </div>

        {/* SMILES */}
        <div>
          <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>SMILES</div>
          <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#9ca3af', backgroundColor: '#0f172a', padding: '4px 8px', borderRadius: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {detail.smiles}
          </div>
        </div>

        {/* Notes */}
        {detail.notes && (
          <div style={{ fontSize: 12, color: '#9ca3af' }}>Note: {detail.notes}</div>
        )}

        {/* Submissions */}
        {detail.submissions.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Submissions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {detail.submissions.map(sub => (
                <div key={sub.submission_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#d1d5db' }}>
                  <span>{sub.assay_name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {sub.completed_at && <span style={{ fontSize: 10, color: '#6b7280' }}>{formatDate(sub.completed_at)}</span>}
                    <StatusBadge status={sub.status} colors={SUB_STATUS_COLORS} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistics */}
        {detail.statistics.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Assay Results</div>
            {detail.statistics.map(stat => (
              <div key={stat.stat_id} style={{ padding: 8, backgroundColor: 'rgba(13, 148, 136, 0.06)', borderRadius: 6, marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#e5e7eb', marginBottom: 4 }}>{stat.assay_name}</div>
                {stat.mean_value != null && (
                  <div style={{ fontSize: 16, fontFamily: 'monospace', fontWeight: 700, color: '#0d9488', marginBottom: 4 }}>
                    {stat.measurement_type} = {stat.mean_value.toFixed(1)} nM
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 12px', fontSize: 11, color: '#9ca3af' }}>
                  {stat.curve_fit_r2 != null && <span>R&sup2; = <b style={{ color: r2Color(stat.curve_fit_r2) }}>{stat.curve_fit_r2.toFixed(4)}</b></span>}
                  {stat.hill_slope != null && <span>Hill = {stat.hill_slope.toFixed(2)}</span>}
                  {stat.n_replicates != null && <span>n = {stat.n_replicates}</span>}
                  {stat.std_deviation != null && <span>SD = {stat.std_deviation.toFixed(2)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dose-response charts */}
        {chartLoading && (
          <div style={{ height: 250, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Loading chart...</span>
          </div>
        )}
        {chartData && (chartData.assays || []).map((assay: any, ai: number) => {
          const dataPoints = assay.data_points || [];
          if (dataPoints.length === 0) return null;
          const matchingStat = detail.statistics.find(s => s.assay_name === assay.assay_name);
          if (!matchingStat) return null;
          return (
            <div key={ai}>
              {(chartData.assays || []).length > 1 && (
                <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{assay.assay_name}</div>
              )}
              <DoseResponseChart
                dataPoints={dataPoints}
                statistics={matchingStat}
                batchCode={detail.batch_code}
                assayName={assay.assay_name}
              />
            </div>
          );
        })}

        {/* ELN Notebook Entries */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
          <ELNSection
            batchCode={detail.batch_code}
            smiles={detail.smiles}
            projectId={detail.project_id}
            latestAssay={detail.submissions.length > 0 ? detail.submissions[0].assay_name : undefined}
          />
        </div>
      </div>
    );
  };

  // =====================================================================
  // RENDER
  // =====================================================================

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Batch Management"
        subtitle="Track synthesis batches, assay submissions, and dose-response data"
        breadcrumbs={[{ label: 'Batches' }]}
      />

      {/* Summary stats bar */}
      <div className="flex-shrink-0 px-4 py-2" style={{ borderBottom: '1px solid var(--card-border, rgba(255,255,255,0.06))' }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {/* Project selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Project</span>
            <select
              value={selectedProject || ''}
              onChange={e => { setSelectedProject(Number(e.target.value)); setExpandedCode(null); }}
              style={{
                padding: '4px 8px', fontSize: 12, borderRadius: 4,
                backgroundColor: 'var(--card-bg, #1a1f2e)', color: 'var(--text-primary, #e5e7eb)',
                border: '1px solid rgba(255,255,255,0.1)', outline: 'none',
              }}
            >
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div style={{ width: 1, height: 20, backgroundColor: 'rgba(255,255,255,0.08)' }} />

          {/* Counts */}
          <StatPill label="Total" value={totalCount} color="#9ca3af" />
          <StatPill label="Active" value={statusCounts['active'] || 0} color="#6b7280" />
          <StatPill label="Submitted" value={statusCounts['submitted'] || 0} color="#f59e0b" />
          <StatPill label="Registered" value={statusCounts['registered'] || 0} color="#10b981" />

          <div style={{ flex: 1 }} />

          {/* Search */}
          <input
            placeholder="Search batches..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              padding: '5px 10px', fontSize: 12, borderRadius: 6, width: 200,
              backgroundColor: 'var(--card-bg, #1a1f2e)', color: 'var(--text-primary, #e5e7eb)',
              border: '1px solid rgba(255,255,255,0.1)', outline: 'none',
            }}
          />

          {/* + New Batch button */}
          {projects.length === 0 ? (
            <span style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>Create a project first</span>
          ) : (
            <button
              onClick={() => { setCreateProjectId(selectedProject); setShowCreateModal(true); }}
              style={{
                padding: '5px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                backgroundColor: '#0D9488', color: '#fff', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0f766e'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0D9488'; }}
            >
              + New Batch
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex-shrink-0 px-4 flex gap-0.5" style={{ borderBottom: '1px solid var(--card-border, rgba(255,255,255,0.06))' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 500,
              color: activeTab === tab.id ? '#0D9488' : 'var(--text-secondary, #9ca3af)',
              borderBottom: activeTab === tab.id ? '2px solid #0D9488' : '2px solid transparent',
              background: 'transparent', border: 'none', borderBottomStyle: 'solid',
              cursor: 'pointer', transition: 'color 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto" style={{ padding: 16 }}>
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#9ca3af' }}>
            Loading batches...
          </div>
        )}

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#ef4444' }}>
            {error}
          </div>
        )}

        {!loading && !error && filteredBatches.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 8 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5" width={40} height={40}>
              <path d="M9 3H3.6a.6.6 0 0 0-.6.6v16.8a.6.6 0 0 0 .6.6H9m0-18h12.4a.6.6 0 0 1 .6.6v16.8a.6.6 0 0 1-.6.6H9m0-18v18" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div style={{ fontSize: 14, color: '#9ca3af' }}>
              {searchQuery ? 'No batches match your search' : 'No synthesis batches in this project'}
            </div>
            {!searchQuery && (
              <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', maxWidth: 340 }}>
                This page tracks synthesis and screening batches. For inventory stock batches, see the{' '}
                <span style={{ color: '#14b8a6', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/inventory')}>Inventory page</span>.
              </div>
            )}
          </div>
        )}

        {!loading && !error && filteredBatches.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '160px 80px 70px 1fr 80px 80px 100px 120px',
              gap: 8, padding: '6px 12px',
              fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div>Batch Code</div>
              <div>Chemist</div>
              <div>Structure</div>
              <div>SMILES</div>
              <div>Purity</div>
              <div>Amount</div>
              <div>Status</div>
              <div>Created</div>
            </div>

            {/* Table rows */}
            {filteredBatches.map(batch => {
              const isExpanded = expandedCode === batch.batch_code;
              return (
                <div key={batch.batch_id}>
                  <div
                    onClick={() => handleRowClick(batch.batch_code)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '160px 80px 70px 1fr 80px 80px 100px 120px',
                      gap: 8, padding: '10px 12px', cursor: 'pointer',
                      backgroundColor: isExpanded ? 'rgba(13, 148, 136, 0.08)' : 'transparent',
                      borderBottom: isExpanded ? 'none' : '1px solid rgba(255,255,255,0.04)',
                      transition: 'background-color 0.15s',
                      alignItems: 'center',
                    }}
                    onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                  >
                    <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: 'var(--text-primary, #e5e7eb)' }}>
                      {batch.batch_code}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>{batch.chemist_initials}</div>
                    <div style={{ width: 60, height: 50, borderRadius: 4, overflow: 'hidden', backgroundColor: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {batch.smiles && <StructureImage smiles={batch.smiles} size={50} />}
                    </div>
                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {batch.smiles}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', fontFamily: 'monospace' }}>
                      {batch.purity_percent != null ? `${batch.purity_percent}%` : '\u2014'}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', fontFamily: 'monospace' }}>
                      {batch.amount_mg != null ? `${batch.amount_mg}` : '\u2014'}
                    </div>
                    <div>
                      <StatusBadge status={batch.status} colors={STATUS_COLORS} />
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{formatDate(batch.created_at)}</div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div style={{
                      backgroundColor: 'rgba(13, 148, 136, 0.04)',
                      borderLeft: '3px solid #0d9488',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      marginBottom: 2,
                    }}>
                      {renderDetail()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Batch Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 100000, backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => { if (!creating) setShowCreateModal(false); }}
        >
          <div
            className="rounded-xl shadow-2xl w-full max-w-lg mx-4"
            style={{ backgroundColor: 'var(--card-bg, #1a1f2e)', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary, #e5e7eb)' }}>New Batch</h3>
              <p className="text-xs mt-1" style={{ color: '#6b7280' }}>A unique batch code will be auto-generated from your initials.</p>
            </div>

            <div className="px-6 py-4" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Row: Initials + Project */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text-primary, #1E293B)', display: 'block', marginBottom: 3 }}>Chemist Initials *</label>
                  <input
                    value={createInitials}
                    onChange={(e) => setCreateInitials(e.target.value.slice(0, 5))}
                    placeholder="e.g. RLXX"
                    style={{
                      width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6,
                      backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--text-primary, #1E293B)',
                      border: '1px solid var(--input-border, #CBD5E1)', outline: 'none',
                      textTransform: 'uppercase',
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text-primary, #1E293B)', display: 'block', marginBottom: 3 }}>Project *</label>
                  <select
                    value={createProjectId || ''}
                    onChange={(e) => setCreateProjectId(Number(e.target.value))}
                    style={{
                      width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6,
                      backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--text-primary, #1E293B)',
                      border: '1px solid var(--input-border, #CBD5E1)', outline: 'none',
                    }}
                  >
                    <option value="">Select project...</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* SMILES */}
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-primary, #1E293B)', display: 'block', marginBottom: 3 }}>Compound SMILES *</label>
                <input
                  value={createSmiles}
                  onChange={(e) => setCreateSmiles(e.target.value)}
                  placeholder="e.g. CC(=O)Oc1ccccc1C(O)=O"
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 6,
                    backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--text-primary, #1E293B)',
                    border: '1px solid var(--input-border, #CBD5E1)', outline: 'none',
                    fontFamily: 'JetBrains Mono, monospace',
                  }}
                />
              </div>

              {/* Row: Purity + Amount + Date */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text-primary, #1E293B)', display: 'block', marginBottom: 3 }}>Purity (%)</label>
                  <input
                    type="number"
                    value={createPurity}
                    onChange={(e) => setCreatePurity(e.target.value)}
                    placeholder="99.5"
                    min="0" max="100" step="0.1"
                    style={{
                      width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6,
                      backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--text-primary, #1E293B)',
                      border: '1px solid var(--input-border, #CBD5E1)', outline: 'none',
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text-primary, #1E293B)', display: 'block', marginBottom: 3 }}>Amount (mg)</label>
                  <input
                    type="number"
                    value={createAmount}
                    onChange={(e) => setCreateAmount(e.target.value)}
                    placeholder="50"
                    min="0" step="0.1"
                    style={{
                      width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6,
                      backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--text-primary, #1E293B)',
                      border: '1px solid var(--input-border, #CBD5E1)', outline: 'none',
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text-primary, #1E293B)', display: 'block', marginBottom: 3 }}>Synthesis Date</label>
                  <input
                    type="date"
                    value={createDate}
                    onChange={(e) => setCreateDate(e.target.value)}
                    style={{
                      width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6,
                      backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--text-primary, #1E293B)',
                      border: '1px solid var(--input-border, #CBD5E1)', outline: 'none',
                    }}
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-primary, #1E293B)', display: 'block', marginBottom: 3 }}>Notes</label>
                <textarea
                  value={createNotes}
                  onChange={(e) => setCreateNotes(e.target.value)}
                  placeholder="Optional synthesis notes..."
                  rows={2}
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6, resize: 'none',
                    backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--text-primary, #1E293B)',
                    border: '1px solid var(--input-border, #CBD5E1)', outline: 'none',
                  }}
                />
              </div>

              {/* Error */}
              {createError && (
                <div style={{ padding: '6px 10px', borderRadius: 6, fontSize: 12, backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}>
                  {createError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                style={{
                  padding: '7px 16px', fontSize: 13, borderRadius: 6,
                  color: '#9ca3af', background: 'transparent', border: 'none', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBatch}
                disabled={creating || !createInitials.trim() || !createSmiles.trim() || !createProjectId}
                style={{
                  padding: '7px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6,
                  backgroundColor: (!createInitials.trim() || !createSmiles.trim() || !createProjectId) ? '#374151' : '#0D9488',
                  color: '#fff', border: 'none', cursor: 'pointer',
                  opacity: creating ? 0.6 : 1,
                  transition: 'background-color 0.15s',
                }}
              >
                {creating ? 'Creating...' : 'Create Batch'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Small sub-components
// =====================================================================

const StatusBadge: React.FC<{ status: string; colors: Record<string, { bg: string; text: string }> }> = ({ status, colors }) => {
  const c = colors[status] || { bg: '#374151', text: '#d1d5db' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 500, textTransform: 'capitalize',
      backgroundColor: c.bg, color: c.text,
    }}>
      {status.replace('_', ' ')}
    </span>
  );
};

const StatPill: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <span style={{ fontSize: 11, color: '#6b7280' }}>{label}</span>
    <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color }}>{value}</span>
  </div>
);

const MetaCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1 }}>{label}</div>
    <div style={{ fontSize: 12, color: '#d1d5db' }}>{value}</div>
  </div>
);

export default BatchesPage;
